import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { ImportJob } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ImportModeBadge } from './import-mode-badge';
import { JobStatusBadge } from './job-status-badge';

export function useImportColumns(onOpen: (job: ImportJob) => void) {
  const { t } = useTranslation('importsExports');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<ImportJob>>>(
    () => [
      {
        id: 'kind',
        header: t('columns.kind'),
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <span className="font-medium">{t(`kinds.${row.original.kind}`)}</span>
            <ImportModeBadge job={row.original} />
          </span>
        ),
        meta: { label: t('columns.kind'), filterKey: 'kind', lockVisibility: true },
        size: 150,
      },
      {
        id: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <JobStatusBadge status={row.original.status} />,
        meta: { label: t('columns.status'), filterKey: 'status' },
        size: 110,
      },
      {
        id: 'progress',
        header: t('columns.processed'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {t('progress', {
              processed: format.number(row.original.processedRows),
              total: format.number(row.original.totalRows),
            })}
          </span>
        ),
        meta: { align: 'end' },
        size: 110,
      },
      {
        id: 'errors',
        header: t('columns.errors'),
        cell: ({ row }) => (
          <span
            className={
              row.original.errorRows > 0
                ? 'text-destructive tabular-nums'
                : 'text-muted-foreground tabular-nums'
            }
          >
            {format.number(row.original.errorRows)}
          </span>
        ),
        meta: { align: 'end' },
        size: 80,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: t('columns.created'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.createdAt)}>
            {format.relative(row.original.createdAt)}
          </span>
        ),
        meta: { sortField: 'createdAt', filterKey: 'createdAt' },
        size: 130,
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">{t('columns.actions')}</span>,
        meta: { label: t('columns.actions') },
        cell: ({ row }) => (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(row.original);
            }}
          >
            {t('details.open')}
          </Button>
        ),
        size: 90,
      },
    ],
    [format, onOpen, t],
  );
}
